import type { Holder, RegistryState, WholesaleCertificate } from '../../data/types';

export type WholesaleMode = 'ANY_CONTACT' | 'PER_INVESTOR_TYPE' | 'MANUAL';

export const WHOLESALE_MODE_LABELS: Record<WholesaleMode, string> = {
  ANY_CONTACT: 'any one person or the entity qualifies',
  PER_INVESTOR_TYPE: 'the entity, or every controlling person, by investor type',
  MANUAL: 'set by hand by staff',
};

export interface WholesaleStatus {
  qualified: boolean;
  route: string;
  governing?: { name: string; expiresAt?: string };
  missing: string[];
  manual: boolean;
}

const isValid = (certificate: WholesaleCertificate | undefined, asAt: string) =>
  Boolean(certificate && (!certificate.expiresAt || certificate.expiresAt >= asAt));

export function controllersOf(holder: Holder) {
  return holder.beneficialOwners.filter((owner) => owner.role !== 'BENEFICIARY');
}

export function wholesaleStatus(
  holder: Holder,
  mode: WholesaleMode,
  asAt: string,
): WholesaleStatus {
  if (mode === 'MANUAL') {
    const override = holder.wholesaleOverride;
    return override
      ? {
          qualified: override.qualified,
          route: `Set by ${override.by}: ${override.reason}`,
          missing: [],
          manual: true,
        }
      : { qualified: false, route: 'Not yet set by staff', missing: [], manual: true };
  }
  if (isValid(holder.wholesale, asAt)) {
    return {
      qualified: true,
      route: holder.type === 'INDIVIDUAL' ? 'Own certificate' : "The entity's own certificate",
      governing: { name: holder.name, expiresAt: holder.wholesale?.expiresAt },
      missing: [],
      manual: false,
    };
  }
  const people = controllersOf(holder);
  const certified = people.filter((person) => isValid(person.wholesale, asAt));
  const latest = (list: typeof people) =>
    [...list].sort((left, right) =>
      (right.wholesale?.expiresAt ?? '9999').localeCompare(left.wholesale?.expiresAt ?? '9999'),
    )[0];
  const earliest = (list: typeof people) =>
    [...list].sort((left, right) =>
      (left.wholesale?.expiresAt ?? '9999').localeCompare(right.wholesale?.expiresAt ?? '9999'),
    )[0];
  if (mode === 'ANY_CONTACT' && certified.length) {
    const best = latest(certified);
    return {
      qualified: true,
      route: `${best.name}'s certificate`,
      governing: { name: best.name, expiresAt: best.wholesale?.expiresAt },
      missing: [],
      manual: false,
    };
  }
  if (
    mode === 'PER_INVESTOR_TYPE' &&
    holder.type !== 'INDIVIDUAL' &&
    people.length &&
    certified.length === people.length
  ) {
    const first = earliest(certified);
    return {
      qualified: true,
      route: 'Every controlling person is certified',
      governing: { name: first.name, expiresAt: first.wholesale?.expiresAt },
      missing: [],
      manual: false,
    };
  }
  const missing =
    holder.type === 'INDIVIDUAL' || people.length === 0
      ? [holder.name]
      : mode === 'ANY_CONTACT'
        ? [holder.name, ...people.map((person) => person.name)]
        : people.filter((person) => !isValid(person.wholesale, asAt)).map((person) => person.name);
  return { qualified: false, route: 'No valid certificate', missing, manual: false };
}

export function wholesaleModeFor(state: RegistryState, fundId?: string): WholesaleMode {
  const setup = fundId ? state.fundSetups.find((item) => item.fundId === fundId) : undefined;
  const value =
    setup?.values['inv.wholesaleMode'] ??
    state.houseDefaults[state.currentTenantId]?.['inv.wholesaleMode'];
  return value === 'ANY_CONTACT' || value === 'MANUAL' ? value : 'PER_INVESTOR_TYPE';
}
